import type { Locale } from './locale'
import { pluralize } from '../lib/format'

// French (fr-CA) unit labels with correct singular/plural. Calculator logic keeps
// stable English unit keys (e.g. 'roll'); this translates them at display time so
// French plurals are right (rouleau → rouleaux) without touching the math.
const FR_UNITS: Record<string, [singular: string, plural: string]> = {
  bag: ['sac', 'sacs'],
  base: ['base', 'bases'],
  board: ['planche', 'planches'],
  bottle: ['bouteille', 'bouteilles'],
  box: ['boîte', 'boîtes'],
  bucket: ['seau', 'seaux'],
  bundle: ['paquet', 'paquets'],
  cap: ['capuchon', 'capuchons'],
  cloth: ['toile', 'toiles'],
  cover: ['manchon', 'manchons'],
  fastener: ['attache', 'attaches'],
  gal: ['gal', 'gal'],
  gallon: ['gallon', 'gallons'],
  hanger: ['étrier', 'étriers'],
  kit: ['trousse', 'trousses'],
  lb: ['lb', 'lb'],
  liner: ['doublure', 'doublures'],
  litre: ['litre', 'litres'],
  pair: ['paire', 'paires'],
  piece: ['pièce', 'pièces'],
  post: ['poteau', 'poteaux'],
  rail: ['lisse', 'lisses'],
  roll: ['rouleau', 'rouleaux'],
  screw: ['vis', 'vis'],
  section: ['section', 'sections'],
  set: ['ensemble', 'ensembles'],
  sheet: ['feuille', 'feuilles'],
  sponge: ['éponge', 'éponges'],
  staple: ['agrafe', 'agrafes'],
  stick: ['baguette', 'baguettes'],
  strip: ['bande', 'bandes'],
  tub: ['seau', 'seaux'],
  tube: ['tube', 'tubes'],
}

// English units that DO NOT change form in the plural — abbreviations, symbol
// units, and measurement shorthand. "27 cu ft", not "27 cu fts". "5 m²",
// not "5 m²s". Treat these as invariant so the naive -s rule skips them.
const EN_INVARIANT = new Set<string>([
  // imperial volume / area shorthand
  'cu ft', 'cu yd', 'cu in', 'cu m', 'cu cm',
  'sq ft', 'sq yd', 'sq in', 'sq m', 'sq cm',
  // length abbreviations
  'ft', 'yd', 'in',
  // metric length
  'm', 'cm', 'mm', 'km',
  // symbol units
  'm²', 'm³', 'ft²', 'ft³', 'cm²', 'cm³',
  // mass / weight abbreviations
  'lb', 'kg', 'g', 'oz',
  // fluid volume abbreviations
  'gal', 'qt', 'pt', 'L', 'mL',
])

// English units whose plural is NOT formed by simply adding 's'.
const EN_IRREGULAR: Record<string, string> = {
  box: 'boxes', // "5 boxes", not "5 boxs"
}

// Display a unit for a quantity in the active locale.
export function unitLabel(unit: string, qty: number, locale: Locale): string {
  // Always show the litre as the capital "L" symbol — never the spelled-out
  // word or a lowercase "l", which reads as a "1" and confused users. Same in
  // EN and FR (the SI symbol is locale-invariant). Likewise abbreviate the
  // gallon to "gal" so the buy lines read consistently (gal + qt, L).
  if (unit === 'litre') return 'L'
  if (unit === 'gallon') return 'gal'
  if (locale === 'fr') {
    const pair = FR_UNITS[unit]
    if (pair) return Math.abs(qty) === 1 ? pair[0] : pair[1]
    return unit // unknown unit: pass through
  }
  if (EN_INVARIANT.has(unit)) return unit
  const irregular = EN_IRREGULAR[unit]
  if (irregular) return Math.abs(qty) === 1 ? unit : irregular
  return pluralize(qty, unit)
}
